import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, BookOpen, Sparkles, Clock, Users } from "lucide-react";

// Modules whose paid content is published in the player.
const PLAYABLE_IDS = new Set(["module-1", "module-2", "module-3", "module-4", "module-5", "module-6"]);

export default function Dashboard() {
    const { user, purchasedCourseIds, licenseOwner, refresh } = useAuth();
    const [serverCourses, setServerCourses] = useState([]);

    useEffect(() => {
        refresh();
        api.get("/me/courses").then(({ data }) => setServerCourses(data.courses || []));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // AUTHORITATIVE ownership set (from JWT-authenticated /auth/me).
    const ownedIdSet = useMemo(
        () => new Set(purchasedCourseIds || []),
        [purchasedCourseIds],
    );

    // Library = intersection of BOTH server-authoritative sources.
    // A course must appear in /me/courses AND in purchasedCourseIds to be shown.
    const library = useMemo(
        () => (serverCourses || []).filter((c) => ownedIdSet.has(c.id)),
        [serverCourses, ownedIdSet],
    );

    const empty = library.length === 0;

    // Triple-defense: only unlock the "Open module" CTA if the course id is
    // (1) in the render list, (2) in ownedIdSet (from /auth/me), AND
    // (3) content is actually published. Any single failure hides the CTA.
    const canOpen = (courseId) => ownedIdSet.has(courseId) && PLAYABLE_IDS.has(courseId);

    return (
        <div className="bg-background">
            <section className="border-b border-black/10 bg-secondary">
                <div className="container-edge py-12 lg:py-16">
                    <p className="overline">Dashboard</p>
                    <h1
                        data-testid="dashboard-greeting"
                        className="mt-3 font-heading font-black text-4xl lg:text-5xl tracking-tighter"
                    >
                        Welcome, {user?.full_name?.split(" ")[0] || "Faculty"}.
                    </h1>
                    <p className="mt-3 text-muted-foreground max-w-2xl">
                        {empty
                            ? "You don't own any modules yet. Browse the catalog to get started."
                            : "Your library. Only modules you own appear here."}
                    </p>
                    {licenseOwner && (
                        <Link
                            to="/license/seats"
                            data-testid="dashboard-manage-seats"
                            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-klein text-klein text-sm font-semibold hover:bg-klein hover:text-white transition-colors"
                        >
                            <Users size={16} /> Manage School Site License seats <ArrowRight size={14} />
                        </Link>
                    )}
                </div>
            </section>

            <section className="container-edge py-16">
                {empty ? (
                    <div
                        data-testid="dashboard-empty"
                        className="border border-black/15 p-10 max-w-2xl"
                    >
                        <BookOpen className="text-klein" size={32} />
                        <h2 className="mt-5 font-heading font-bold text-2xl tracking-tight">
                            No modules in your library yet.
                        </h2>
                        <p className="mt-3 text-muted-foreground">
                            Pick an individual module or grab the full bundle to unlock it here.
                        </p>
                        <Link
                            to="/courses"
                            data-testid="dashboard-browse"
                            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                        >
                            Browse modules <ArrowRight size={16} />
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
                            {library.map((c) => {
                                const openable = canOpen(c.id);
                                return (
                                    <article
                                        key={c.id}
                                        data-testid={`owned-course-${c.id}`}
                                        className="bg-white p-8 flex flex-col"
                                    >
                                        <div className="flex items-start justify-between">
                                            <span className="font-mono text-xs tracking-widest text-klein">
                                                MOD / {c.number}
                                            </span>
                                            {openable ? (
                                                <Sparkles size={16} className="text-klein" />
                                            ) : (
                                                <Clock size={16} className="text-muted-foreground" />
                                            )}
                                        </div>
                                        <h3 className="mt-5 font-heading font-bold text-2xl tracking-tight leading-tight">
                                            {c.title}
                                        </h3>
                                        <p className="mt-1 text-xs uppercase tracking-widest text-klein">
                                            {c.tagline}
                                        </p>
                                        <p className="mt-4 text-sm text-muted-foreground leading-relaxed">
                                            {c.description}
                                        </p>
                                        <div className="mt-auto pt-8">
                                            {openable ? (
                                                <Link
                                                    to={`/learn/${c.id}`}
                                                    data-testid={`open-module-${c.id}`}
                                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                                                >
                                                    Open module <ArrowRight size={14} />
                                                </Link>
                                            ) : (
                                                <div
                                                    data-testid={`coming-soon-${c.id}`}
                                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-black/20 text-sm font-semibold text-muted-foreground cursor-not-allowed"
                                                >
                                                    <Clock size={14} /> Content coming soon
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        <div className="mt-10">
                            <Link
                                to="/courses"
                                data-testid="dashboard-browse-more"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-klein hover:text-black transition-colors underline-offset-4 hover:underline"
                            >
                                Browse more modules <ArrowRight size={14} />
                            </Link>
                        </div>
                    </>
                )}
            </section>
        </div>
    );
}
